import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { useTranslation, Trans } from 'react-i18next'
import * as S from './SectionVantagensInstituicao.styles'

const KEYS = [
  'acesso',
  'multilingue',
  'integracao',
  'treinamento',
  'ensino',
  'colecoes'
]

const IMAGES: Record<string, string> = {
  acesso: 'acesso.png',
  multilingue: 'multilingue.png',
  integracao: 'integracao.png',
  treinamento: 'treinamento.png',
  ensino: 'ensino-diversificado.png',
  colecoes: 'colecoes.png'
}

const ContentRenderer = ({ sectionKey }: { sectionKey: string }) => {
  switch (sectionKey) {
    case 'acesso':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.acesso.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.BulletList>
            <li>
              <Trans
                i18nKey="landingV2.sectionVantagensInstituicao.content.acesso.b1"
                components={{ bold: <strong /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey="landingV2.sectionVantagensInstituicao.content.acesso.b2"
                components={{ bold: <strong /> }}
              />
            </li>
          </S.BulletList>
        </>
      )
    case 'multilingue':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.multilingue.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.BulletList>
            <li>
              <Trans
                i18nKey="landingV2.sectionVantagensInstituicao.content.multilingue.b1"
                components={{ bold: <strong /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey="landingV2.sectionVantagensInstituicao.content.multilingue.b2"
                components={{ bold: <strong /> }}
              />
            </li>
            <li>
              <Trans
                i18nKey="landingV2.sectionVantagensInstituicao.content.multilingue.b3"
                components={{ bold: <strong /> }}
              />
            </li>
          </S.BulletList>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.multilingue.pAfter"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
        </>
      )
    case 'integracao':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.integracao.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.integracao.p2"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
        </>
      )
    case 'treinamento':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.treinamento.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.treinamento.p2"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
        </>
      )
    case 'ensino':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.ensino.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.ensino.p2"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.ensino.p3"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
        </>
      )
    case 'colecoes':
      return (
        <>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.colecoes.p1"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.colecoes.p2"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
          <S.Paragraph>
            <Trans
              i18nKey="landingV2.sectionVantagensInstituicao.content.colecoes.p3"
              components={{ bold: <strong /> }}
            />
          </S.Paragraph>
        </>
      )
    default:
      return null
  }
}

const SectionVantagensInstituicao = () => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState(KEYS[0])
  const [isMobile, setIsMobile] = useState(false)
  const buttonsRowRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 900px)').matches)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    if (!isMobile || !buttonsRowRef.current) return
    const el = buttonsRowRef.current
    let isDown = false
    let startX: number
    let scrollLeft: number
    const sensitivity = 0.5

    const onMouseDown = (e: MouseEvent) => {
      isDown = true
      el.classList.add('dragging')
      startX = e.pageX - el.offsetLeft
      scrollLeft = el.scrollLeft
    }
    const onMouseLeave = () => {
      isDown = false
      el.classList.remove('dragging')
    }
    const onMouseUp = () => {
      isDown = false
      el.classList.remove('dragging')
    }
    const onMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - el.offsetLeft
      const walk = (x - startX) * sensitivity
      el.scrollTo({ left: scrollLeft - walk, behavior: 'smooth' })
    }

    el.addEventListener('mousedown', onMouseDown)
    el.addEventListener('mouseleave', onMouseLeave)
    el.addEventListener('mouseup', onMouseUp)
    el.addEventListener('mousemove', onMouseMove)

    return () => {
      el.removeEventListener('mousedown', onMouseDown)
      el.removeEventListener('mouseleave', onMouseLeave)
      el.removeEventListener('mouseup', onMouseUp)
      el.removeEventListener('mousemove', onMouseMove)
    }
  }, [isMobile])

  return (
    <S.Section>
      <S.Title>{t('landingV2.sectionVantagensInstituicao.title')}</S.Title>
      <S.ButtonsRow ref={buttonsRowRef}>
        {KEYS.map(key => (
          <S.Button
            key={key}
            active={selected === key}
            onClick={() => isMobile && setSelected(key)}
            onMouseEnter={() => !isMobile && setSelected(key)}
          >
            {t(`landingV2.sectionVantagensInstituicao.buttons.${key}`)}
          </S.Button>
        ))}
      </S.ButtonsRow>
      <S.ContentRow>
        <S.TextCol>
          <ContentRenderer sectionKey={selected} />
        </S.TextCol>
        <S.ImageCol>
          <Image
            src={`${process.env.CDN_URL}/public/landingpagev2/images/carrousel-home/${IMAGES[selected]}`}
            alt={t(`landingV2.sectionVantagensInstituicao.buttons.${selected}`)}
            width={640}
            height={360}
            style={{ maxWidth: '100%', height: 'auto' }}
            priority
          />
        </S.ImageCol>
      </S.ContentRow>
    </S.Section>
  )
}

export default SectionVantagensInstituicao
